import { Button } from '@/components/UI/Button'
import { Dialog } from '@/components/UI/Dialog'
import type { PhotoFrameLockReason } from '@/schemas/v2/spot'
import { IconLock } from '@tabler/icons-react'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  onClose: () => void
  /** storybookで確認する際に、スクロール可にするためのフラグ。raundで利用する場合にはtrue */
  scrollLock?: boolean
  /** スタンプ/ポイントラベル（例: "スタンプ" / "ポイント" / "スタンプ・ポイント"）。未指定時は "スタンプ" */
  stampLabel?: string
  /** ロック理由。未指定時はスタンプ未獲得として扱う（取得失敗時のフォールバック） */
  reason?: PhotoFrameLockReason | null
  /**
   * 位置情報を取得できていないか。
   * サーバーは座標が無い場合も一律 out_of_range を返すため、これが true のときは
   * 「範囲外」ではなく「位置情報が取得できていない」ことを案内する（#3445）。
   */
  locationUnavailable?: boolean
}

/**
 * ARフォトフレームがロックされている状態を表示するダイアログ。
 * スタンプ未獲得・範囲外・位置情報未取得で文言を出し分ける。
 * Figma node: 551:17760
 * TODO: Dialogにタイトル無しでアイコンを付与できるようにする
 */
export const DialogArPhotoLocked = memo(
  ({ onClose, scrollLock = false, stampLabel, reason = null, locationUnavailable = false }: Props) => {
    const { t } = useTranslation()

    // 未指定時に {{what}} が空文字へ補間され「ARフォトフレームは 獲得後に…」と主語が欠けるため、
    // 呼び出し側が省略した場合は既定のラベルへフォールバックする
    const label = stampLabel ?? t('common.stamp', 'スタンプ')

    const message =
      reason === 'out_of_range'
        ? locationUnavailable
          ? t(
              'spot_detail.location_unavailable_message',
              '位置情報を取得できないため\nご利用いただけません。\n端末の位置情報設定をご確認ください。',
            )
          : t('spot_detail.ar_photo.out_of_range_message', 'ARフォトフレームは\n有効範囲内で\nご利用いただけます')
        : t('spot_detail.ar_photo.locked_message', 'ARフォトフレームは\n{{what}}獲得後に\nご利用いただけます', {
            what: label,
          })

    return (
      <Dialog selectorState={true} compact showCloseButton={false} lockScroll={scrollLock} closeAction={onClose}>
        <div className="flex flex-col items-center gap-[16px] pt-2">
          <div className="flex items-center justify-center w-[64px] h-[64px] rounded-full bg-[#f5f5f5]">
            <IconLock size={32} className="text-[#757575]" />
          </div>

          <p className="text-center text-[14px] font-bold text-black leading-[22px]">{message}</p>

          <Button label={t('common.close', '閉じる')} variant="primary" size="md" onClick={onClose} fullWidth />
        </div>
      </Dialog>
    )
  },
)
